import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ref, get } from 'firebase/database';
import { realtimeDb } from '../firebase/config';
import { FaStar, FaShoppingBag } from 'react-icons/fa';
import './ProductRecommendations.css';

// Import product images
import whiteness from '../assets/whiteness.webp';
import relax from '../assets/whiteness relaxation.webp';
import multicare from '../assets/whiteness multi care .webp';
import triaction from '../assets/triaction.webp';
import puresoft from '../assets/pure soft 2.webp';
import pureSoft from '../assets/sensiplus puresoft.webp';
import dinokids from '../assets/dino kids.webp';
import bambosalt from '../assets/Bamboo Salt.webp';

const ProductRecommendations = ({ currentProduct = null, maxItems = 8 }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Define all helper functions first
  const getAllProducts = () => {
    return [
      {
        id: 1,
        name: "WHITENESS",
        price: 100,
        image: whiteness,
        category: "adult",
        type: "whiteness",
        rating: 4.5,
        sales: 150
      },
      {
        id: 2,
        name: "WHITENESS RELAXATION",
        price: 120,
        image: relax,
        category: "adult",
        type: "whiteness",
        rating: 4.3,
        sales: 120
      },
      {
        id: 3,
        name: "WHITENESS MULTI CARE",
        price: 140,
        image: multicare,
        category: "adult",
        type: "whiteness",
        rating: 4.6,
        sales: 180
      },
      {
        id: 4,
        name: "TRIACTION",
        price: 160,
        image: triaction,
        category: "adult",
        type: "triaction",
        rating: 4.4,
        sales: 90
      },
      {
        id: 5,
        name: "PURE SOFT 2",
        price: 80,
        image: puresoft,
        category: "adult",
        type: "puresoft",
        rating: 4.2,
        sales: 200
      },
      {
        id: 6,
        name: "SENSI PLUS PURE SOFT",
        price: 95,
        image: pureSoft,
        category: "adult",
        type: "puresoft",
        rating: 4.1,
        sales: 160
      },
      {
        id: 7,
        name: "DINO KIDS",
        price: 60,
        image: dinokids,
        category: "kids",
        type: "kids",
        rating: 4.7,
        sales: 200
      },
      {
        id: 8,
        name: "BAMBOO SALT 512345",
        price: 120,
        image: bambosalt,
        category: "adult",
        type: "bamboo",
        rating: 4.3,
        sales: 90
      }
    ];
  };

  const getPopularProducts = useCallback(() => {
    return getAllProducts()
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, maxItems);
  }, [maxItems]);

  const removeDuplicates = (products) => {
    const seen = new Set();
    return products.filter(product => {
      const duplicate = seen.has(product.id);
      seen.add(product.id);
      return !duplicate;
    });
  };

  const getPopularProductsInCategories = (categories, allProducts) => {
    return allProducts
      .filter(product => categories.includes(product.category))
      .sort((a, b) => (b.rating || 0) - (a.rating || 0));
  };

  const findProductsByPreferences = (preferences, allProducts) => {
    const sortedCategories = Object.entries(preferences.categories)
      .sort(([,a], [,b]) => b - a)
      .map(([category]) => category);

    const sortedTypes = Object.entries(preferences.types)
      .sort(([,a], [,b]) => b - a)
      .map(([type]) => type);

    return allProducts
      .filter(product => 
        sortedCategories.includes(product.category) ||
        sortedTypes.includes(product.type)
      )
      .sort((a, b) => {
        const aScore = (sortedCategories.indexOf(a.category) + 1) + (sortedTypes.indexOf(a.type) + 1);
        const bScore = (sortedCategories.indexOf(b.category) + 1) + (sortedTypes.indexOf(b.type) + 1);
        return bScore - aScore;
      });
  };

  const findSimilarProducts = (currentProduct, allProducts) => {
    return allProducts
      .filter(product => 
        product.category === currentProduct.category ||
        product.type === currentProduct.type
      )
      .sort((a, b) => {
        // Prioritize same category and type
        const aScore = (a.category === currentProduct.category ? 2 : 0) + 
                      (a.type === currentProduct.type ? 1 : 0);
        const bScore = (b.category === currentProduct.category ? 2 : 0) + 
                      (b.type === currentProduct.type ? 1 : 0);
        return bScore - aScore;
      });
  };

  const analyzeUserPreferences = (purchaseHistory, recentlyViewed) => {
    const preferences = {
      categories: {},
      types: {},
      priceRange: { min: 0, max: 0 },
      totalSpent: 0
    };

    // Analyze purchase history
    purchaseHistory.forEach(order => {
      if (order.items) {
        order.items.forEach(item => {
          // Category preference
          preferences.categories[item.category] = (preferences.categories[item.category] || 0) + 1;
          
          // Type preference
          preferences.types[item.type] = (preferences.types[item.type] || 0) + 1;
          
          // Price analysis
          const price = parseFloat(item.price) || 0;
          if (preferences.priceRange.max === 0 || price > preferences.priceRange.max) {
            preferences.priceRange.max = price;
          }
          if (preferences.priceRange.min === 0 || price < preferences.priceRange.min) {
            preferences.priceRange.min = price;
          }
          
          preferences.totalSpent += price;
        });
      }
    });

    // Analyze recently viewed
    if (Array.isArray(recentlyViewed)) {
      recentlyViewed.forEach(item => {
        preferences.categories[item.category] = (preferences.categories[item.category] || 0) + 0.5;
        preferences.types[item.type] = (preferences.types[item.type] || 0) + 0.5;
      });
    }

    return preferences;
  };

  const generateRecommendations = useCallback((purchaseHistory, recentlyViewed, currentProduct) => {
    const allProducts = getAllProducts();
    const recommendations = [];
    const userPreferences = analyzeUserPreferences(purchaseHistory, recentlyViewed);
    
    // Filter out current product if viewing a product page
    const availableProducts = currentProduct 
      ? allProducts.filter(p => p.id !== currentProduct.id)
      : allProducts;

    // 1. Similar products based on current product
    if (currentProduct) {
      const similarProducts = findSimilarProducts(currentProduct, availableProducts);
      recommendations.push(...similarProducts);
    }

    // 2. Products based on user preferences
    const preferenceBasedProducts = findProductsByPreferences(userPreferences, availableProducts);
    recommendations.push(...preferenceBasedProducts);

    // 3. Popular products in user's preferred categories
    const popularInPreferences = getPopularProductsInCategories(userPreferences.categories, availableProducts);
    recommendations.push(...popularInPreferences);

    // Remove duplicates and limit
    const uniqueRecommendations = removeDuplicates(recommendations);
    return uniqueRecommendations.slice(0, maxItems);
  }, [maxItems]);

  const loadRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get user's purchase history
      const ordersRef = ref(realtimeDb, `orders/${currentUser.uid}`);
      const ordersSnapshot = await get(ordersRef);
      
      let recommendations = [];
      
      if (ordersSnapshot.exists()) {
        const orders = ordersSnapshot.val();
        const purchaseHistory = Object.values(orders);
        
        // Get recently viewed products
        const recentRef = ref(realtimeDb, `recentlyViewed/${currentUser.uid}`);
        const recentSnapshot = await get(recentRef);
        let recentlyViewed = [];
        
        if (recentSnapshot.exists()) {
          recentlyViewed = recentSnapshot.val();
        }

        // Generate recommendations based on purchase history and recently viewed
        recommendations = generateRecommendations(purchaseHistory, recentlyViewed, currentProduct);
      } else {
        // No purchase history, show popular products
        recommendations = getPopularProducts();
      }
      
      setRecommendations(recommendations.slice(0, maxItems));
    } catch (error) {
      console.error('Error loading recommendations:', error);
      // Fallback to popular products
      setRecommendations(getPopularProducts().slice(0, maxItems));
    } finally {
      setLoading(false);
    }
  }, [currentUser, currentProduct, maxItems, generateRecommendations, getPopularProducts]);

  const loadPopularProducts = useCallback(() => {
    // For guest users, show popular products
    const popularProducts = getPopularProducts();
    setRecommendations(popularProducts.slice(0, maxItems));
    setLoading(false);
  }, [maxItems, getPopularProducts]);

  useEffect(() => {
    if (currentUser) {
      loadRecommendations();
    } else {
      // For guest users, show popular products
      loadPopularProducts();
    }
  }, [currentUser, currentProduct, loadRecommendations, loadPopularProducts]);

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const getRecommendationTitle = () => {
    if (!currentUser) return "Popular Products";
    if (currentProduct) return "You Might Also Like";
    return "Recommended for You";
  };

  if (loading) {
    return (
      <div className="product-recommendations">
        <div className="recommendations-header">
          <h3>Loading Recommendations...</h3>
        </div>
        <div className="recommendations-loading">Loading...</div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="product-recommendations">
      <div className="recommendations-header">
        <h3>
          <FaStar className="header-icon" />
          {getRecommendationTitle()}
        </h3>
        <span className="recommendations-count">{recommendations.length} items</span>
      </div>
      
      <div className="recommendations-grid">
        {recommendations.map((product) => (
          <div 
            key={product.id} 
            className="recommendation-card"
            onClick={() => handleProductClick(product.id)}
          >
            <div className="product-image-container">
              <img 
                src={product.image} 
                alt={product.name}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/120x120?text=Product';
                }}
              />
              {product.rating && (
                <div className="product-rating">
                  <FaStar />
                  {product.rating}
                </div>
              )}
            </div>
            
            <div className="product-info">
              <h4 className="product-name">{product.name}</h4>
              <div className="product-category">{product.category}</div>
              <div className="product-price">₹{product.price}</div>
              {product.sales && (
                <div className="product-sales">
                  <FaShoppingBag />
                  {product.sales} sold
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductRecommendations; 